import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useTheme } from '@/context/ThemeContext';
import { Calendar, ChevronDown, ChevronUp, Clock, Edit, Notebook, Table2 } from 'lucide-react';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { IPBSClient } from '../../types/common/IPBSClient';
import { CurrentUserContext } from '@/context/CurrentUserContext';

interface ClientCardProps {
  client: IPBSClient;
}

const seededColor = (name: string, isDark: boolean) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash) % 360;
  return `hsl(${h}, 55%, ${isDark ? 35 : 72}%)`;
};

const ClientCard: React.FC<ClientCardProps> = ({ client }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { isDarkMode } = useTheme();
  const { currentUser } = useContext(CurrentUserContext);
  const iconButtons = useMemo(() => {
    let options: { icon: JSX.Element, title: string, url: string }[] = []
    let blockedBPRoles: string[] = ['Assistant', 'Registered Behavior Technician']

    if (!client?.id) return options

    if (!blockedBPRoles.includes(currentUser.Role)) {
      options.push({
        icon: <Edit className="h-4 w-4" />,
        title: 'Behavior Plan',
        url: `https://behaviorplan.teampbs.com/behaviorPlans?cid=${client.id}`
      })
    }
    options = [...options,
    {
      icon: <Table2 className="h-4 w-4" />,
      title: 'Data Collection',
      url: `https://datacollection.teampbs.com/dashboard/${client.id}`
    },
    {
      icon: <Calendar className="h-4 w-4" />,
      title: 'Availability',
      url: `https://availability.teampbs.com/Availability/?userType=0&cid=${client.encryptedId}`
    },
    {
      icon: <Clock className="h-4 w-4" />,
      title: 'Time Entry',
      url: `https://pbsapps.teampbs.com/timeentry/client/#/?clientid=${client.id}`
    },
    {
      icon: <Notebook className="h-4 w-4" />,
      title: 'Client File',
      url: `https://clientform.teampbs.com/?ID=${client.id}`
    }]
    return options;
  }, [client?.id, client?.encryptedId, currentUser.Role]);

  const openLink = useCallback((link: string) => {
    window.open(link, '_blank');
  }, []);

  const handleItemClick = useCallback((url: string) => {
    let args: string[] = url.split(':');
    if (args.length > 1 && args[0] === 'pbslive') {
      switch (args[1]) {
        case 'treatmentteam':
          if (window.openChatDirectlyWithTreatmentGroup) {
            window.openChatDirectlyWithTreatmentGroup(client.id);
          }
          break;
        default:
          break;
      }
    }
    else {
      openLink(url);
    }
  }, [openLink, client.id])
  const getClientFullName = useCallback(() => {
    if (!client) return '';
    //client name comes in as "LastName, FirstName", we want to display it as "FirstName LastName"

    const parts = client.name.split(',').map(part => part.trim());
    if (parts.length === 2) {
      return `${parts[1]} ${parts[0]}`;
    }
    return client.name;
  }, [client]);

  if (!client || typeof client !== 'object' || !client.id || !client.name) {
    return null;
  }

  return (
    <DropdownMenu onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="m-2 gap-2 min-h-[48px] px-3 rounded"
          style={{ backgroundColor: seededColor(client.name, isDarkMode) }}
        >
          {getClientFullName()}
          {menuOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {iconButtons.map(({ icon, title, url }) => (
          <DropdownMenuItem key={title} onClick={() => handleItemClick(url)}>
            {icon}
            {title}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ClientCard;
